import Button from "@mui/material/Button";
import React, { useEffect, useMemo, useRef, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import AppBar from "./AppBar";
import useTemplateInstance from "../../../hooks/useTemplateInstance";
import {
  ACTIONS,
  getFirstRenderPartByResource,
  getPartRefById,
  getResource,
} from "../../../../../Providers/TemplateInstanceProvider";
import { Trans, useTranslation } from "react-i18next";
import Icon from "@mui/material/Icon";
import { Bubble, BubbleList } from "./Bubble";
import {
  ToolbarOption,
  ToolbarOptions,
  ToolbarOptionsHeader,
} from "./ToolBarOptions";
import { Slider } from "@mui/material";
import useDebounce from "../../../../../hooks/useDebounce";
import TemplateContainer from "./TemplateContainer";

import { templateFontList } from "../common";
import {
  getTextDimensions,
  getTextLines,
} from "../../../../../components/Template/stringsutils";
import { upperCaseFirst } from "../../../../../lib/common";
import ToolbarContainer from "./ToolbarContainer";
import ToolbarContent from "./ToolbarContent";
import ActionsContainer from "./ActionsContainer";
import ExitDialog from "./ExitDialog";
import emojiRegex from "emoji-regex";
import { removeInvalidTemplateTextCharacters } from "@/react/components/Template/stringutils";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },

  sliderContainer: {
    display: "flex",
    justifyContent: "center",
  },
  sliderInnerContainer: {
    width: "80%",
  },
  colorList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    flexWrap: "nowrap",
    overflow: "auto",
    "&::before, &::after": {
      content: '""' /* Insert pseudo-element */,
      margin: "auto",
    },
    "& > li": {
      marginLeft: theme.spacing(2),
      display: "inline-block",
    },
    "& button": {
      backgroundColor: "transparent",
      border: "none",
      padding: "5px",
    },
  },
  colorCircle: {
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    padding: "3px",
    backgroundColor: (props) => {
      console.log("The props", props.color);
      return props.color;
    },
    border: (props) =>
      `1px solid ${
        props.color == "white" || props.color?.toLowerCase() == "#ffffff"
          ? "#ccc"
          : props.color
      }`,
    outlineOffset: "3px",
    outline: (props) => (props.selected ? "1px solid #707070" : "none"),
  },
}));

export default ({ changeMenu }) => {
  const classes = useStyles();
  const [state, dispatch] = useTemplateInstance();
  const [toolbar, setToolbar] = useState("main");
  const activeTextInputRef = useRef();
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showSaveChangesDialog, setShowSaveChangesDialog] = useState(false);
  const [localResource, setLocalResource] = useState(null);
  const [isInitialized, setInitialized] = useState(false);

  const renderElement = useMemo(() => {
    const partRef = getPartRefById(state.currentPartRefId, state.template);
    console.log(
      "The part ref",
      partRef,
      state.template,
      state.currentResourceId,
    );
    const resource = getResource(partRef, state.currentResourceId, state);
    return getFirstRenderPartByResource(resource, state.template);
  }, [state.currentPartRefId]);

  const stateResource = useMemo(() => {
    const partRef = getPartRefById(state.currentPartRefId, state.template);
    console.log(
      "The part ref",
      partRef,
      state.template,
      state.currentResourceId,
    );
    return getResource(partRef, state.currentResourceId, state);
  }, []);

  useEffect(() => {
    // Autofocus text if it's empty
    setTimeout(() => {
      let localResource = { ...stateResource };

      // Make sure we can fit the text:
      let tooLarge = true;
      let fontSize = parseInt(renderElement.font.size);
      while (tooLarge && fontSize > 12) {
        const dim = getTextDimensions(
          renderElement.font.family,
          "Text",
          fontSize,
          "pt",
          2000,
        );
        if (dim.height > renderElement.size.height) {
          console.log("Reducing font size", fontSize);
          fontSize--;
        } else {
          tooLarge = false;
        }
      }

      let font = { ...localResource.font };
      if (fontSize < parseInt(renderElement.font.size)) {
        console.log("Setting override size", fontSize);
        font = {
          ...font,
          overrideSize: fontSize,
        };
      }
      setLocalResource({
        ...localResource,
        font: font,
      });
    }, 0);
  }, []);

  useEffect(() => {
    if (localResource && !isInitialized && localResource.font.text == "") {
      activeTextInputRef.current.focus();
      setInitialized(true);
    }
  }, [localResource]);

  const handleCancel = (e) => {
    e.stopPropagation();

    if (dirty) {
      setShowSaveChangesDialog(true);
    } else {
      setLocalResource(null);
      setTimeout(() => {
        changeMenu("main");
      }, 300);
    }
  };

  const doClose = () => {
    setShowSaveChangesDialog(false);
    setLocalResource(null);
    setTimeout(() => {
      changeMenu("main");
    }, 300);
  };

  const handleTextInputFocus = () => {
    setKeyboardVisible(true);
  };

  const handleLinesUpdated = () => {
    const fontFamily =
      localResource.font.overrideFamily || renderElement.font.family;
    const fontSize =
      localResource.font.overrideSize || renderElement.font.size;

    const text = localResource.font.text
      .replace(/^[\s\n]+/, "")
      .replace(/[\s\n]+$/, "");

    const lines = getTextLines(
      fontFamily,
      text,
      fontSize,
      "pt",
      renderElement.size.width,
    );

    console.log("Saving lines!", lines);
    handleValueChange("lines")(lines);

    // We might have removed some spaces:
    handleValueChange("text")(text);
  };

  const handleTextInputBlur = () => {
    setKeyboardVisible(false);
    handleLinesUpdated();
  };

  const handleTextInputChange = (text) => {
    // Hack to avoid people using space to align:
    const newValue = removeInvalidTemplateTextCharacters(text);

    console.log("The input is", text, newValue);
    handleValueChange("text")(newValue);
  };

  const handleValueChange = (what) => {
    return (value) => {
      setDirty(true);
      setLocalResource((resource) => {
        const newResource = {
          ...resource,
          font: {
            ...resource.font,
            [what]: value,
          },
        };
        console.log("Value change on text", newResource);
        return newResource;
      });
    };
  };

  const handleComplete = () => {
    dispatch({
      type: ACTIONS.UPDATE_CURRENT_TEXT,
      font: localResource.font,
    });
    setLocalResource(null);
    setTimeout(() => {
      changeMenu("main");
    }, 300);
  };

  const handleSelectText = () => {
    activeTextInputRef.current.focus();
  };

  return (
    <div className={classes.mainContainer}>
      <TemplateContainer
        zoomResource={localResource}
        activeTextInputRef={activeTextInputRef}
        handleTextInputChange={handleTextInputChange}
        handleTextInputFocus={handleTextInputFocus}
        handleTextInputBlur={handleTextInputBlur}
      />

      {!keyboardVisible && (
        <ShowToolbar
          toolbar={toolbar}
          resource={localResource || stateResource}
          changeMenu={changeMenu}
          handleSelectText={handleSelectText}
          handleValueChange={handleValueChange}
          handleChangeToolbar={(val) => setToolbar(val)}
          renderElement={renderElement}
          handleCancel={handleCancel}
          handleComplete={handleComplete}
          handleLinesUpdated={handleLinesUpdated}
        />
      )}
      <ExitDialog
        open={showSaveChangesDialog}
        handleCancel={() => setShowSaveChangesDialog(false)}
        handleOk={doClose}
      />
    </div>
  );
};

const ShowToolbar = (props) => {
  switch (props.toolbar) {
    case "main":
      return <MainToolbar {...props} />;
    case "text-align":
      return <TextAlignToolbar {...props} />;
    case "text-size":
      return <TextSizeToolbar {...props} />;
    case "font-family":
      return <FontFamilyToolbar {...props} />;
    case "text-color":
      return <TextColorToolbar {...props} />;
  }
};

const MainToolbar = ({
  resource,
  handleCancel,
  handleComplete,
  handleChangeToolbar,
  handleSelectText,
  renderElement,
}) => {
  const { t } = useTranslation();
  const align =
    resource?.font.overrideHorizontalAlign ||
    renderElement.font.horizontalAlign;
  const fontFamily =
    resource?.font.overrideFamily || renderElement.font.family;
  const currentColor =
    resource?.font.overrideColor || renderElement.font.color;

  let alignIcon = "format_align_left";
  if (align == "center") {
    alignIcon = "format_align_center";
  } else if (align == "right") {
    alignIcon = "format_align_right";
  }

  return (
    <ToolbarContainer>
      <ToolbarContent>
        <BubbleList>
          <Bubble
            header={t("Enter text")}
            content={<Icon>text_fields</Icon>}
            onClick={handleSelectText}
          />
          <Bubble
            header={t("Font")}
            content={
              <div style={{ fontSize: "15px", fontFamily: fontFamily }}>
                {upperCaseFirst(fontFamily.toLowerCase())}
              </div>
            }
            disabled={!resource.font.text}
            onClick={(e) => handleChangeToolbar("font-family")}
          />
          <Bubble
            header={t("Align text")}
            content={<Icon>{alignIcon}</Icon>}
            disabled={!resource.font.text}
            onClick={(e) => handleChangeToolbar("text-align")}
          />
          <Bubble
            header={t("Font size")}
            content={<Icon>format_size</Icon>}
            disabled={!resource.font.text}
            onClick={(e) => handleChangeToolbar("text-size")}
          />
          <Bubble
            header={t("Color")}
            content={
              <div style={{ display: "flex", alignItems: "center" }}>
                <ColorCircle color={currentColor} />
              </div>
            }
            disabled={!resource.font.text}
            onClick={(e) => handleChangeToolbar("text-color")}
          />
        </BubbleList>
      </ToolbarContent>
      <ActionsContainer>
        <Button color="primary" onClick={handleCancel}>
          <Trans>Cancel</Trans>
        </Button>
        <Button color="primary" onClick={handleComplete}>
          <Trans>Done</Trans>
        </Button>
      </ActionsContainer>
    </ToolbarContainer>
  );
};

const ColorCircle = (props) => {
  const classes = useStyles(props);
  return <div className={classes.colorCircle}></div>;
};

const TextAlignToolbar = ({
  resource,
  handleChangeToolbar,
  handleValueChange,
  renderElement,
}) => {
  const { t } = useTranslation();
  const fontCopy = useMemo(() => ({ ...resource.font }), []);

  const handleCancel = () => {
    handleValueChange("overrideHorizontalAlign")(
      fontCopy.overrideHorizontalAlign,
    );
    handleChangeToolbar("main");
  };

  const handleComplete = () => {
    handleChangeToolbar("main");
  };

  const handleClick = (align) => {
    handleValueChange("overrideHorizontalAlign")(align);
  };

  const align =
    resource.font.overrideHorizontalAlign ||
    renderElement.font.horizontalAlign;

  return (
    <ToolbarContainer>
      <ToolbarContent>
        <ToolbarOptions header={t("Choose text alignment")}>
          <ToolbarOption
            content={<Icon>format_align_left</Icon>}
            selected={align == "left"}
            onClick={() => handleClick("left")}
          />
          <ToolbarOption
            content={<Icon>format_align_center</Icon>}
            selected={align == "center"}
            onClick={() => handleClick("center")}
          />
          <ToolbarOption
            content={<Icon>format_align_right</Icon>}
            selected={align == "right"}
            onClick={() => handleClick("right")}
          />
        </ToolbarOptions>
      </ToolbarContent>
      <ActionsContainer>
        <Button color="primary" onClick={handleCancel}>
          <Trans>Cancel</Trans>
        </Button>
        <Button color="primary" onClick={handleComplete}>
          <Trans>Done</Trans>
        </Button>
      </ActionsContainer>
    </ToolbarContainer>
  );
};

const TextSizeToolbar = ({
  resource,
  handleChangeToolbar,
  handleValueChange,
  renderElement,
  handleLinesUpdated,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [fontSize, setFontSize] = useState(
    resource.font.overrideSize || renderElement.font.size,
  );
  const debouncedFontSize = useDebounce(fontSize, 200);
  const [lastValidFontSize, setLastValidFontSize] = useState(
    resource.font.overrideSize || renderElement.font.size,
  );
  const fontCopy = useMemo(() => ({ ...resource.font }), []);

  const fontFamily = resource.font.overrideFamily || renderElement.font.family;

  const checkAndSetFontSize = (size) => {
    const dim = getTextDimensions(
      fontFamily,
      resource.font.text,
      size,
      "pt",
      renderElement.size.width,
    );
    if (
      dim.width > renderElement.size.width ||
      dim.height > renderElement.size.height
    ) {
      setFontSize(() => lastValidFontSize);
      return;
    }
    handleValueChange("overrideSize")(size);
    setLastValidFontSize(() => size);
  };

  useEffect(() => {
    checkAndSetFontSize(debouncedFontSize);
  }, [debouncedFontSize]);

  const handleChange = (event, size) => {
    setFontSize(() => size);
  };

  const handleCancel = () => {
    handleValueChange("overrideSize")(fontCopy.overrideSize);
    handleChangeToolbar("main");
  };

  const handleComplete = () => {
    handleLinesUpdated();
    handleChangeToolbar("main");
  };

  return (
    <ToolbarContainer>
      <ToolbarContent>
        <ToolbarOptionsHeader includeContainer={true} header={t("Font size")}>
          <div className={classes.sliderContainer}>
            <div className={classes.sliderInnerContainer}>
              <Slider
                min={12}
                max={110}
                value={fontSize}
                onChangeCommitted={(e, size) => checkAndSetFontSize(size)}
                onChange={handleChange}
              />
            </div>
          </div>
        </ToolbarOptionsHeader>
      </ToolbarContent>
      <ActionsContainer>
        <Button color="primary" onClick={handleCancel}>
          <Trans>Cancel</Trans>
        </Button>
        <Button color="primary" onClick={handleComplete}>
          <Trans>Done</Trans>
        </Button>
      </ActionsContainer>
    </ToolbarContainer>
  );
};

const FontFamilyToolbar = ({
  resource,
  handleValueChange,
  handleChangeToolbar,
  renderElement,
  handleLinesUpdated,
}) => {
  const { t } = useTranslation();

  const fontCopy = useMemo(() => ({ ...resource.font }), []);
  const fontFamily = resource.font.overrideFamily || renderElement.font.family;
  const fontSize = resource.font.overrideSize || renderElement.font.size;

  const currentFont = useRef();

  const handleCancel = () => {
    handleValueChange("overrideFamily")(fontCopy.overrideFamily);
    handleChangeToolbar("main");
  };

  const handleComplete = () => {
    handleLinesUpdated();
    handleChangeToolbar("main");
  };

  const handleClick = (family) => {
    const dim = getTextDimensions(
      family,
      resource.font.text,
      fontSize,
      "pt",
      renderElement.size.width,
    );
    if (
      dim.width > renderElement.size.width ||
      dim.height > renderElement.size.height
    ) {
      // Reset font size if it's too large
      handleValueChange("overrideSize")(renderElement.font.size);
    }

    handleValueChange("overrideFamily")(family);
  };

  useEffect(() => {
    if (currentFont.current) {
      currentFont.current.scrollIntoView();
    }
  }, [fontFamily]);

  const fonts = templateFontList;

  return (
    <ToolbarContainer>
      <ToolbarContent>
        <ToolbarOptions header={t("Choose font")}>
          {fonts.map((font) => {
            const isSelected = fontFamily.toLowerCase() == font.toLowerCase();
            return (
              <ToolbarOption
                key={font}
                buttonStyle={{ fontFamily: font }}
                forwardRef={isSelected ? currentFont : null}
                content={font}
                onClick={() => handleClick(font)}
                selected={isSelected}
              />
            );
          })}
        </ToolbarOptions>
      </ToolbarContent>
      <ActionsContainer>
        <Button color="primary" onClick={handleCancel}>
          <Trans>Cancel</Trans>
        </Button>
        <Button color="primary" onClick={handleComplete}>
          <Trans>Done</Trans>
        </Button>
      </ActionsContainer>
    </ToolbarContainer>
  );
};

const TextColorToolbar = ({
  resource,
  handleChangeToolbar,
  handleValueChange,
  renderElement,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const fontCopy = useMemo(() => ({ ...resource.font }), []);

  const currentColor = resource.font.overrideColor || renderElement.font.color;

  const handleCancel = () => {
    handleValueChange("overrideColor")(fontCopy.overrideColor);
    handleChangeToolbar("main");
  };

  const handleComplete = () => {
    handleChangeToolbar("main");
  };

  const handleClick = (color) => {
    handleValueChange("overrideColor")(color);
  };

  const colors = useMemo(() => {
    const colors = [
      "#000000",
      "#FFFFFF",
      "#C52B2B",
      "#2B9BC5",
      "#C5BB2B",
      "#A72BC5",
      "#5AB880",
      "#F3E66F",
      "#C52B87",
      "#7673AB",
    ];
    if (colors.indexOf(renderElement.font.color) < 0) {
      return [renderElement.font.color.toUpperCase(), ...colors];
    }
    return colors;
  }, []);

  return (
    <ToolbarContainer>
      <ToolbarContent>
        <ToolbarOptionsHeader
          includeContainer={true}
          header={t("Choose color")}
        >
          <ul className={classes.colorList}>
            {colors.map((color) => {
              return (
                <li key={color}>
                  <button onClick={() => handleClick(color)}>
                    <ColorCircle
                      color={color}
                      selected={currentColor.toUpperCase() == color}
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        </ToolbarOptionsHeader>
      </ToolbarContent>
      <ActionsContainer>
        <Button color="primary" onClick={handleCancel}>
          <Trans>Cancel</Trans>
        </Button>
        <Button color="primary" onClick={handleComplete}>
          <Trans>Done</Trans>
        </Button>
      </ActionsContainer>
    </ToolbarContainer>
  );
};
